import React, { useState } from 'react';
import { Mui } from '@osu/react-ui';
import { DecisionOutcome } from '../decisions';
import { getAdditionalResourcesConfig, getStepDefinition } from '../../utils/stepHelpers';
import { DeclineConfirmationModal } from '../ui';
import { useSubmitStudentDecisionMutation } from '../../../services/graphql/api';

/**
 * Template for "Decision" step content
 * Receives pre-processed content and actions as props from step building logic
 * Derives decision value from applicationData.selfService.currentStep
 * @param {Object} props
 * @param {Object} props.applicationData - Application data with selfService.currentStep
 * @param {string} [props.stepId] - Step identifier for content lookup
 * @param {Object} props.stepContent - Pre-processed content from step building
 * @param {Array} props.stepActions - Pre-processed actions from step building
 * @param {function} [props.onAction] - Action handler
 * @param {boolean} [props.isAdminView] - Whether component is being rendered in admin view mode
 */
function DecisionTemplate({ 
  applicationData, 
  stepId = 'honors-decision',
  stepContent, // Pre-processed content from step building
  stepActions, // Pre-processed actions from step building
  onAction,
  isAdminView: isAdminViewProp // Explicit prop for admin view
}) {
  // State for decline confirmation modal
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // RTK Query mutation hook
  const [submitStudentDecision] = useSubmitStudentDecisionMutation();

  // Use the isAdminView prop if provided, otherwise default to false
  // Note: ReadOnlyEnforcer handles button disabling at a higher level for admin mode
  const isAdminView = isAdminViewProp || false;
  // Get current step from application data for configuration access
  const currentStep = applicationData?.selfService?.currentStep;
  
  const resourcesConfig = getAdditionalResourcesConfig(currentStep);
  const stepDefinition = getStepDefinition(currentStep);
  const modalConfig = stepDefinition?.declineModal;

  // Handle decline honors action
  const handleDeclineHonors = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await submitStudentDecision({
        decision: 'DECLINE_HONORS'
      }).unwrap();

      console.log('Decline honors successful:', result);
      setSubmitSuccess(true);
      
      // Close modal after successful submission
      setShowDeclineModal(false);
      
      // Call parent onAction handler if provided
      if (onAction) {
        onAction('decline-honors', { type: 'decline-honors', success: true, result });
      }
    } catch (error) {
      console.error('Failed to decline honors:', error);
      setSubmitError(error.message || 'Failed to decline honors offer. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced action handler to handle decline-honors action
  const handleAction = (actionType, action) => {
    if (actionType === 'decline-honors') {
      // Prevent decline action in admin view
      if (isAdminView) {
        console.log('Decline action blocked in admin view mode');
        return;
      }
      setShowDeclineModal(true);
      return;
    }

    // Pass other actions to parent handler
    if (onAction) {
      onAction(actionType, action);
    }
  };



  return (
    <Mui.Box>
      {/* Success message for decline submission */}
      {submitSuccess && (
        <Mui.Alert severity="success" sx={{ mb: 2 }}>
          Your decision has been submitted successfully.
        </Mui.Alert>
      )}

      {/* Error message for decline submission */}
      {submitError && (
        <Mui.Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Mui.Alert>
      )}

      {/* Decision outcome component - lead with the meaningful content */}
      <DecisionOutcome
        stepId={stepId}
        applicationData={applicationData}
        stepContent={stepContent} // Pass pre-computed content
        stepActions={stepActions} // Pass pre-computed actions
        onAction={handleAction} // Use our enhanced action handler
        isAdminView={isAdminView} // Pass admin view state
      />
      
      {/* Additional resources - only show for non-denied decisions or make them more relevant */}
      <Mui.Box sx={{ mt: 3 }}>
        <Mui.Typography variant="h6" gutterBottom>
          Additional Resources
        </Mui.Typography>

        <Mui.Grid container spacing={2}>
          <Mui.Grid item xs={12} sm={6}>
            <Mui.Card variant="outlined">
              <Mui.CardContent>
                <Mui.Typography variant="subtitle1" gutterBottom>
                  {resourcesConfig.programInfo.title}
                </Mui.Typography>
                <Mui.Typography variant="body2" color="textSecondary">
                  {resourcesConfig.programInfo.description}
                </Mui.Typography>
                <Mui.Button
                  size="small"
                  sx={{ mt: 1 }}
                  href={resourcesConfig.programInfo?.href || (currentStep?.includes('SCHOLARS') ? 'https://honors-scholars.osu.edu/scholars' : 'https://honors-scholars.osu.edu/honors')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resourcesConfig.programInfo.buttonText}
                </Mui.Button>
              </Mui.CardContent>
            </Mui.Card>
          </Mui.Grid>

          <Mui.Grid item xs={12} sm={6}>
            <Mui.Card variant="outlined">
              <Mui.CardContent>
                <Mui.Typography variant="subtitle1" gutterBottom>
                  {resourcesConfig.academicSupport.title}
                </Mui.Typography>
                <Mui.Typography variant="body2" color="textSecondary">
                  {resourcesConfig.academicSupport.description}
                </Mui.Typography>
                <Mui.Button
                  size="small"
                  sx={{ mt: 1 }}
                  href={resourcesConfig.academicSupport.href || "https://undergrad.osu.edu"}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {resourcesConfig.academicSupport.buttonText}
                </Mui.Button>
              </Mui.CardContent>
            </Mui.Card>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.Box>

      {/* Decline Confirmation Modal */}
      <DeclineConfirmationModal
        open={showDeclineModal}
        onClose={() => setShowDeclineModal(false)}
        onConfirm={handleDeclineHonors}
        modalConfig={modalConfig}
        isSubmitting={isSubmitting}
      />
    </Mui.Box>
  );
}

export default DecisionTemplate;